import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

// The level of assistance is the number in "(n)" at the start, 1 to 7.
const SIX_MINUTE_WALK_TEST_LEVEL_REGEX = /^\s*\(\s*(\d+)\s*\)/;
const SIX_MINUTE_WALK_TEST_TOTAL_ASSISTANCE = 1;

// Titles separate the instruction with "||" or "‖".
const SIX_MINUTE_WALK_TEST_TITLE_SEPARATOR = /\|\||‖/;

// The distance in metres is the score; 0 is a real distance.
const SIX_MINUTE_WALK_TEST_DISTANCE = 'question828';
const SIX_MINUTE_WALK_TEST_LEVEL = 'question829';

// The distance is whole metres from 0 to 1000.
const SIX_MINUTE_WALK_TEST_DISTANCE_PATTERN = /^\d+$/;
const SIX_MINUTE_WALK_TEST_MAX_DISTANCE = 1000;

function distanceValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  return !value ||
    (SIX_MINUTE_WALK_TEST_DISTANCE_PATTERN.test(value) &&
      Number(value) <= SIX_MINUTE_WALK_TEST_MAX_DISTANCE)
    ? null
    : { distance: true };
}

@Component({
  selector: 'app-six-minute-walk-test-questions',
  templateUrl: './six-minute-walk-test-questions.component.html',
  styleUrl: './six-minute-walk-test-questions.component.css',
  standalone: false,
})
export class SixMinuteWalkTestQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the test's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;
  // The last allowed distance, restored when a typed digit goes over 1000.
  private lastDistance = '';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const distance = this.formGroup.get(SIX_MINUTE_WALK_TEST_DISTANCE);
    distance?.addValidators(distanceValidator);
    distance?.updateValueAndValidity({ emitEvent: false });
    this.lastDistance = String(distance?.value ?? '');

    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.keepDistanceInRange();
      this.applyRules();
    });
    this.applyRules();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(SIX_MINUTE_WALK_TEST_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(SIX_MINUTE_WALK_TEST_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isDistanceElement(element: IQuestionnaireQuestion): boolean {
    return element.name === SIX_MINUTE_WALK_TEST_DISTANCE;
  }

  isInvalid(element: IQuestionnaireQuestion): boolean {
    return !!this.formGroup.get(element.name)?.invalid;
  }

  // The distance is locked at 0 while the patient needs total assistance.
  isLocked(element: IQuestionnaireQuestion): boolean {
    return this.isDistanceElement(element) && this.isTotalAssistance();
  }

  // Only digits can be typed or pasted, and never a distance over 1000.
  private keepDistanceInRange(): void {
    const distance = this.formGroup.get(SIX_MINUTE_WALK_TEST_DISTANCE);
    if (!distance || distance.disabled) return;

    const value = String(distance.value ?? '');
    let allowed = value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    if (allowed && Number(allowed) > SIX_MINUTE_WALK_TEST_MAX_DISTANCE) {
      allowed = this.lastDistance;
    }

    if (allowed !== value) {
      distance.setValue(allowed, { emitEvent: false });
    }
    this.lastDistance = allowed;
  }

  private applyRules(): void {
    const distance = this.formGroup.get(SIX_MINUTE_WALK_TEST_DISTANCE);
    if (!distance) return;

    if (this.isTotalAssistance()) {
      if (distance.value !== '0') {
        distance.setValue('0', { emitEvent: false });
      }
      distance.disable({ emitEvent: false });
    } else if (
      distance.disabled &&
      this.formGroup.get(SIX_MINUTE_WALK_TEST_LEVEL)?.enabled
    ) {
      // Unlocked only when the form itself can be edited.
      distance.enable({ emitEvent: false });
    }

    this.cdr.detectChanges();
  }

  private isTotalAssistance(): boolean {
    const selectedValue = this.formGroup.get(SIX_MINUTE_WALK_TEST_LEVEL)?.value;
    const choice = (
      this.elements?.find((el) => el.name === SIX_MINUTE_WALK_TEST_LEVEL)
        ?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(SIX_MINUTE_WALK_TEST_LEVEL_REGEX);

    return !!match && Number(match[1]) === SIX_MINUTE_WALK_TEST_TOTAL_ASSISTANCE;
  }
}
